import { useMemo } from "react";
import { Link } from "react-router-dom";
import FirmFlowTable from "../../components/mytasks/FirmFlowTable";
import NewBadge from "../../components/NewBadge";
import { getDueDateStatusFromDate } from "../../helpers/dueDateStatus";
import { formatDate } from "../../helpers/formatDate";
import { formatTimestampDate } from "../../helpers/formatDateTime";
import { sortDueDateRows } from "../../helpers/sortDueDateRows";

const STATUSES = ["To Do", "In Progress", "Complete"];
const MY_ASSIGNMENTS_SERVICES_PATH = "/my-assignments/services";

const getNormalizedStatus = (status) =>
    typeof status === "string" ? status.trim().toLowerCase() : status;

const isCompletedStatus = (status) => {
    const normalizedStatus = getNormalizedStatus(status);

    return (
        normalizedStatus === 2 ||
        normalizedStatus === "complete" ||
        normalizedStatus === "completed"
    );
};

const getServiceLinkId = (row) =>
    row?.clientServiceSubscriptionId ?? row?.id ?? row?.subscriptionId;

const getAssignedByLabel = (value) => {
    if (!value) return "-";

    if (typeof value === "string") {
        return value || "-";
    }

    if (typeof value === "object") {
        if (typeof value.fullName === "string" && value.fullName.trim() !== "") {
            return value.fullName;
        }

        const firstName = value.firstName ?? "";
        const lastName = value.lastName ?? "";
        const fullName = `${firstName} ${lastName}`.trim();

        return fullName || "-";
    }

    return "-";
};

const MyAssignedServicesTable = ({
    onHandleUpdateServiceStatus,
    employeeName,
    data,
    canWriteServices = false,
    canReadServices = false,
}) => {
    const columns = useMemo(
        () => [
            {
                Header: "Client",
                accessor: "client",
                Cell: (props) => {
                    const serviceLinkId = getServiceLinkId(props.row.original);

                    return (
                        <Link
                            to={`/clients/service/${serviceLinkId}/client/${props.row.original.clientId}`}
                            state={{ returnTo: MY_ASSIGNMENTS_SERVICES_PATH }}
                            className="rounded-full text-gray hover:text-blue-500"
                        >
                            {props.row.original.isNew === true ? (
                                <span>
                                    <NewBadge label="New" /> {props.value}
                                </span>
                            ) : (
                                props.value
                            )}
                        </Link>
                    );
                },
            },
            {
                Header: "Year",
                accessor: "year",
                Cell: (props) => {
                    const serviceLinkId = getServiceLinkId(props.row.original);

                    return (
                        <Link
                            to={`/clients/service/${serviceLinkId}/client/${props.row.original.clientId}`}
                            state={{ returnTo: MY_ASSIGNMENTS_SERVICES_PATH }}
                            className="rounded-full text-gray hover:text-blue-500"
                        >
                            {props.value}
                        </Link>
                    );
                },
            },
            {
                Header: "Service",
                accessor: "service",
                Cell: (props) => {
                    const serviceLinkId = getServiceLinkId(props.row.original);

                    return (
                        <Link
                            to={`/clients/service/${serviceLinkId}/client/${props.row.original.clientId}`}
                            state={{ returnTo: MY_ASSIGNMENTS_SERVICES_PATH }}
                            className="rounded-full text-gray hover:text-blue-500"
                        >
                            {props.value}
                        </Link>
                    );
                },
            },
            {
                Header: "Assigned By",
                accessor: "assignedBy",
                Cell: (props) => {
                    const serviceLinkId = getServiceLinkId(props.row.original);

                    return (
                        <Link
                            to={`/clients/service/${serviceLinkId}/client/${props.row.original.clientId}`}
                            state={{ returnTo: MY_ASSIGNMENTS_SERVICES_PATH }}
                            className="rounded-full text-gray hover:text-blue-500"
                        >
                            {getAssignedByLabel(props.value)}
                        </Link>
                    );
                },
            },

            {
                Header: "Due Date",
                accessor: "dueDate",
                Cell: (props) => {
                    const {
                        clientId,
                        dueDateColorClass,
                        dueDateSubLabel,
                        completionLabel,
                        status,
                    } = props.row.original;

                    const serviceLinkId = getServiceLinkId(props.row.original);
                    const href = `/clients/service/${serviceLinkId}/client/${clientId}`;
                    const normalizedStatus = getNormalizedStatus(status);
                    const isComplete =
                        normalizedStatus === "complete" || normalizedStatus === 2;
                    const isTodoOrInProgress =
                        normalizedStatus === "to do" ||
                        normalizedStatus === "in progress" ||
                        normalizedStatus === 0 ||
                        normalizedStatus === 1;
                    const derivedDueDateColorClass =
                        dueDateColorClass ||
                        getDueDateStatusFromDate(props.value)?.bgClass;
                    const badgeColorClass = isComplete
                        ? "bg-slate-500"
                        : derivedDueDateColorClass;

                    return (
                        <Link
                            to={href}
                            state={{ returnTo: MY_ASSIGNMENTS_SERVICES_PATH }}
                            className="inline-flex flex-col items-start"
                        >
                            <span
                                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-md font-medium text-white ${
                                    badgeColorClass || "bg-slate-400"
                                }`}
                            >
                                {formatDate(props.value, {
                                    dateStyle: "short",
                                })}
                            </span>

                            {isTodoOrInProgress && dueDateSubLabel && (
                                <span className="mt-1 text-xs font-medium text-slate-600">
                                    {dueDateSubLabel}
                                </span>
                            )}

                            {isComplete && completionLabel && (
                                <span className="mt-1 text-xs font-medium text-slate-600">
                                    {completionLabel}
                                </span>
                            )}
                        </Link>
                    );
                },
            },
            {
                Header: "Notes",
                accessor: "notes",
                Cell: (props) => {
                    const serviceLinkId = getServiceLinkId(props.row.original);

                    return (
                        <Link
                            to={`/clients/service/${serviceLinkId}/client/${props.row.original.clientId}`}
                            state={{ returnTo: MY_ASSIGNMENTS_SERVICES_PATH }}
                            className="rounded-full text-gray hover:text-blue-500"
                        >
                            {props.value || "-"}
                        </Link>
                    );
                },
            },
            {
                Header: "Status",
                accessor: "status",
                Cell: (props) => (
                    <div>
                        {
                            <Link
                                to={`/clients/service/${getServiceLinkId(
                                    props.row.original,
                                )}/client/${props.row.original.clientId}`}
                                state={{ returnTo: MY_ASSIGNMENTS_SERVICES_PATH }}
                                className="rounded-full text-gray hover:text-blue-500"
                            >
                                <span
                                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-md font-medium text-white ${
                                        STATUSES[props.value] === "To Do"
                                            ? "bg-red-500"
                                            : STATUSES[props.value] == "In Progress"
                                              ? "bg-yellow-500"
                                              : "bg-teal-500"
                                    }`}
                                >
                                    {STATUSES[props.value]}
                                </span>
                            </Link>
                        }
                        {props.value == 0 && canWriteServices && (
                            <button
                                className="ml-2 inline-flex items-center p-1.5 border border-transparent rounded-sm shadow-sm text-white bg-teal-500 hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                                onClick={() =>
                                    onHandleUpdateServiceStatus(
                                        getServiceLinkId(props.row.original),
                                        props.row.original.clientId,
                                    )
                                }
                            >
                                Start
                            </button>
                        )}
                    </div>
                ),
            },
            {
                Header: "Completion Date",
                accessor: "completionDate",
                Cell: (props) => {
                    const { clientId, status, completionDate } = props.row.original;
                    const serviceLinkId = getServiceLinkId(props.row.original);

                    const normalizedStatus = getNormalizedStatus(status);
                    const isComplete =
                        normalizedStatus === "complete" ||
                        normalizedStatus === "completed" ||
                        normalizedStatus === 2;

                    return (
                        <Link
                            to={`/clients/service/${serviceLinkId}/client/${clientId}`}
                            state={{ returnTo: MY_ASSIGNMENTS_SERVICES_PATH }}
                            className="rounded-full text-gray hover:text-blue-500"
                        >
                            {!isComplete ? (
                                <span className="rounded-full text-gray hover:text-blue-500">
                                    Not completed
                                </span>
                            ) : !completionDate ? (
                                <span className="text-slate-500">Not assigned</span>
                            ) : (
                                formatTimestampDate(completionDate)
                            )}
                        </Link>
                    );
                },
            },
        ],
        [employeeName, canWriteServices],
    );

    const filteredData = useMemo(
        () =>
            Array.isArray(data)
                ? data.filter((row) => !isCompletedStatus(row?.status))
                : data,
        [data],
    );
    const sortedData = useMemo(
        () =>
            sortDueDateRows(filteredData, {
                dueDateKeys: ["dueDate", "subscriptionEndDate"],
                yearKeys: ["year", "serviceYear"],
                serviceNameKeys: ["service", "serviceName"],
            }),
        [filteredData],
    );

    if (!canReadServices) return null;

    return (
        <div>
            {filteredData != null ? (
                <FirmFlowTable
                    columns={columns}
                    data={sortedData}
                    employee={employeeName}
                />
            ) : (
                "No data found"
            )}
        </div>
    );
};

export default MyAssignedServicesTable;
